import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  clearAdminSession,
  isAdminAuthLive,
  persistAdminSession,
  readAdminSession,
  requestAdminChangePassword,
  requestAdminLogin,
  requestAdminLogout,
  requestAdminMe,
} from "../api/adminAuth";

/**
 * @typedef {Object} AdminUser
 * @property {string} id
 * @property {string} email
 * @property {'admin'} role
 * @property {string} [displayName]
 * @property {boolean} [mustChangePassword]
 */

const AdminAuthContext = createContext(null);

export function AdminAuthProvider({ children }) {
  const [user, setUser] = useState(() => readAdminSession());

  useEffect(() => {
    if (!isAdminAuthLive()) return undefined;
    const cached = readAdminSession();
    if (!cached) return undefined;
    let cancelled = false;
    requestAdminMe()
      .then(({ user: next }) => {
        if (cancelled) return;
        persistAdminSession(next);
        setUser(next);
      })
      .catch(() => {
        if (cancelled) return;
        clearAdminSession();
        setUser(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async ({ email, password }) => {
    const { user: next } = await requestAdminLogin({ email, password });
    if (next.role !== "admin") {
      throw new Error("This account is not an administrator.");
    }
    persistAdminSession(next);
    setUser(next);
  }, []);

  const logout = useCallback(async () => {
    await requestAdminLogout();
    clearAdminSession();
    setUser(null);
  }, []);

  const changePassword = useCallback(async ({ currentPassword, newPassword }) => {
    const { user: next } = await requestAdminChangePassword({ currentPassword, newPassword });
    persistAdminSession(next);
    setUser(next);
  }, []);

  const value = useMemo(
    () => ({
      user,
      login,
      logout,
      changePassword,
      isAuthenticated: Boolean(user && user.role === "admin"),
    }),
    [user, login, logout, changePassword]
  );

  return <AdminAuthContext.Provider value={value}>{children}</AdminAuthContext.Provider>;
}

export function useAdminAuth() {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) {
    throw new Error("useAdminAuth must be used within AdminAuthProvider");
  }
  return ctx;
}
